import {
  ChangeDetectionStrategy,
  Component, HostListener, ViewEncapsulation, EventEmitter, Output, Inject, Input, OnDestroy
} from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { isNullOrUndefined } from '@app/util/util';
import { FlightType } from '@search-bar/entities/flight-type';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { SearchBarAbstractComponent } from '@search-bar/abstracts/search-bar-abstract.component';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { SearchType } from '@search-bar/entities/search-type';
import { HeaderService } from '@header/services/header.service';
import { ButtonSize } from '@design-system/components/molecules/button/entities/button.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';

@Component({
  selector: 'app-search-bar-desktop',
  templateUrl: './search-bar-desktop.component.html',
  styleUrls: ['./search-bar-desktop.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SearchBarDesktopComponent extends SearchBarAbstractComponent implements OnDestroy {

  @Output() selectedBaggageIncluded: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() baggageIncluded: boolean;

  public selectedArrivalPlace: boolean;
  public showFlightOptionSelection: boolean = false;
  public eventPath: BehaviorSubject<EventTarget[]> = new BehaviorSubject<EventTarget[]>([]);
  public activeOption: string;
  public arrivalPlace: AutocompletePlace;
  public departurePlace: AutocompletePlace;
  public showBaggageIncluded: boolean = false;

  public readonly searchIcon = Icon.Search;

  private subscriptions: Subscription = new Subscription();

  @HostListener('document:click', ['$event'])
  clickout(event: Event): void {
    if (this.isBrowser) {
      this.eventPath.next(event.composedPath());
      const path = event['path'] || (event.composedPath && event.composedPath());
      if (path) {
        const inputButton = path.find(paths =>
          !!paths['classList'] && paths['classList'].length > 0
          && paths['classList'].contains('travel-calendar-cont'));
        if ((!inputButton && this.showFlightOptionSelection) ||
          (inputButton && this.showCalendarInput && this.showFlightOptionSelection)) {
          this.toggleFlightOption();
        }
      }
    }
  }

  constructor(
    private readonly facade: BaseSearchBarFacade,
    private readonly headerService: HeaderService,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) {
    super();
    this.subscriptions.add(this.arrivalPlace$.subscribe(x => this.arrivalPlace = x));
    this.subscriptions.add(this.departurePlace$.subscribe(x => this.departurePlace = x));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get buttonSize(): ButtonSize {
    return this.isResultsPage ? ButtonSize.SquareSM : ButtonSize.SquareRG;
  }

  get calendarField(): typeof CalendarField {
    return CalendarField;
  }

  get showYear(): boolean {
    return this.searchType === SearchType.FLIGHT_RESULTS;
  }

  get departurePlace$(): Observable<AutocompletePlace> {
    return this.facade.departurePlace$;
  }

  get arrivalPlace$(): Observable<AutocompletePlace> {
    return this.facade.arrivalPlace$;
  }

  get showDesktopSearchOptions(): boolean {
    return !this.showCalendarInput && this.showFlightOptionSelection;
  }

  get isArrivalPlaceEmpty(): boolean {
    return this.isEmptyPlace(this.arrivalPlace, this.isSubmitted);
  }

  get hasDeparture(): boolean {
    return !!this.departurePlace && !isNullOrUndefined(this.departurePlace.code);
  }

  get hasArrival(): boolean {
    return !!this.arrivalPlace && !isNullOrUndefined(this.arrivalPlace.code);
  }

  get activateArrivalCursor(): boolean {
    return this.hasDeparture && !this.hasArrival;
  }

  get isDeparturePlaceEmpty(): boolean {
    return this.isEmptyPlace(this.departurePlace, this.isSubmitted);
  }

  get isValidArrivalPlace(): boolean {
    return this.samePlaceError || this.isEmptyArrivalPlace;
  }

  get arrivalPlaceOrDefault(): AutocompletePlace {
    return !isNullOrUndefined(this.arrivalPlace) && !isNullOrUndefined(this.arrivalPlace.code)
      ? this.arrivalPlace : this.defaultPlace;
  }

  get departurePlaceOrDefault(): AutocompletePlace {
    return !isNullOrUndefined(this.departurePlace) && !isNullOrUndefined(this.departurePlace.code)
      ? this.departurePlace : this.defaultPlace;
  }

  get isResultsPage(): boolean {
    return this.searchType === SearchType.FLIGHT_RESULTS || this.searchType === SearchType.BUSES_RESULTS;
  }

  get arrowIcon(): Icon {
    return this.isOneWayTrip || this.destination ? Icon.ArrowRight : Icon.Roundtrip;
  }

  get isEmptyArrivalPlace(): boolean {
    return this.arrivalPlace && isNullOrUndefined(this.arrivalPlace.code) && this.isSubmitted;
  }

  get isEmptyDepartureDate(): boolean {
    return isNullOrUndefined(this.departureDate) && this.isSubmitted;
  }

  get isEmptyArrivalDate(): boolean {
    return isNullOrUndefined(this.arrivalDate) && this.isSubmitted;
  }

  public selectedPlace(value: SelectAutocompletePlace): void {
    this.selectedArrivalPlace = !this.selectedArrivalPlace;
    this.selectPlace.emit(value);
  }

  public hideCalendarInput(): void {
    this.showCalendarInput = false;
  }

  public flightTypeSelected(type: FlightType): void {
    if (this.isResultsPage) this.toggleFlightOption();
    this.selectFlightType.emit(type);
  }

  public flightTypeCalendar(e: FlightType): void {
    if (this.isResultsPage) this.toggleFlightOption();
    this.selectFlightType.emit(e);
  }

  public toggleFlightOption(option?: string): void {
    this.showFlightOptionSelection = !this.showFlightOptionSelection;
    if (option) this.activeOption = option;
  }

  public baggageIncludedChange(value: boolean): void {
    this.selectedBaggageIncluded.emit(value);
  }

  public isActiveFlightType(type: string): boolean {
    return type === this.flightType;
  }

  public applyCalendar(event: Date[]): void {
    this.selectFlightCalendar.emit(event);
    this.hideCalendarInput();
  }

  public swapLocation(): void {
    if (!this.destination) {
      this.selectedArrivalPlace = !this.selectedArrivalPlace;
      this.swapLocations();
    }
  }

  public searchFlights(): void {
    this.isSubmitted = true;
    this.showFlightOptionSelection = false;
    if (!this.disabledButton) {
      this.emitSearch.emit();
    }
  }

  public showSearchInputDeparture(): void {
    this.selectedArrivalPlace = false;
  }

  public showSearchInputArrival(): void {
    this.selectedArrivalPlace = true;
  }

  public goBack(): void {
    this.headerService.goBack();
  }

  public swapLocations(): void {
    this.swapLocationsEmitter.emit();
  }
}
